import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ListField, TextField } from "@/components/admin/cms/CmsFields";
import HistoryDialog from "@/components/admin/cms/HistoryDialog";
import { can, MANAGE_CONTENT } from "@/lib/format";
import { SITE_NAV_KEY, mergeSiteNav } from "@/lib/cms";
import { toast } from "sonner";
import { ExternalLink, Lock, History } from "lucide-react";

const linkListField = (key, label) => ({
  key,
  label,
  type: "list",
  itemLabel: "Link",
  min: 1,
  max: 8,
  newItem: { label: "", to: "/" },
  itemFields: [
    { key: "label", label: "Label", type: "text" },
    { key: "to", label: "Destination (path)", type: "text" },
  ],
});

export default function NavFooterEditor() {
  const { user } = useAdminAuth();
  const editable = can(user?.role, MANAGE_CONTENT);
  const [nav, setNav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/content/${SITE_NAV_KEY}`);
      setNav(mergeSiteNav(r.data && r.data.primary ? r.data : null));
    } catch {
      setNav(mergeSiteNav(null));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setKey = (k, v) => setNav((n) => ({ ...n, [k]: v }));
  const setFooter = (k, v) => setNav((n) => ({ ...n, footer: { ...n.footer, [k]: v } }));
  const setAnnc = (k, v) => setNav((n) => ({ ...n, announcement: { ...n.announcement, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${SITE_NAV_KEY}`, nav);
      toast.success("Navigation & footer updated \u2014 changes are live.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !nav) {
    return <div data-testid="cms-nav-loading" className="text-[var(--muted)] py-8">Loading…</div>;
  }

  return (
    <div data-testid="cms-nav">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wide text-[var(--muted)]">Header navigation, slide-in menu &amp; footer</div>
        <div className="flex items-center gap-2">
          {editable && (
            <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(true)} data-testid="cms-nav-history" className="text-[var(--text-2)] hover:text-[var(--text)]">
              <History size={13} className="mr-1" />History
            </Button>
          )}
          <a href="/" target="_blank" rel="noreferrer" className="text-sm text-[#9a7b3f] hover:opacity-80 transition-opacity flex items-center gap-1" data-testid="cms-nav-viewlive">
            View live <ExternalLink size={13} />
          </a>
          {!editable && (
            <Badge variant="outline" className="bg-[var(--panel-2)] text-[var(--text-2)] border-[var(--border-lch)]">
              <Lock size={11} className="mr-1" />View only
            </Badge>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 max-w-4xl">
        <Card className="border-[var(--border-lch)] p-5 space-y-4">
          <div className="text-sm font-medium text-[var(--text)]">Top navigation</div>
          <ListField field={linkListField("primary", "Header links")} value={nav.primary} onChange={(v) => setKey("primary", v)} disabled={!editable} />
        </Card>

        <Card className="border-[var(--border-lch)] p-5 space-y-4">
          <div className="text-sm font-medium text-[var(--text)]">Slide-in menu</div>
          <ListField field={linkListField("more", "Menu links")} value={nav.more} onChange={(v) => setKey("more", v)} disabled={!editable} />
        </Card>

        <Card className="border-[var(--border-lch)] p-5 space-y-4">
          <div className="text-sm font-medium text-[var(--text)]">Footer links</div>
          <ListField field={linkListField("footer_links", "Footer links")} value={nav.footer_links} onChange={(v) => setKey("footer_links", v)} disabled={!editable} />
        </Card>

        <Card className="border-[var(--border-lch)] p-5 space-y-4">
          <div className="text-sm font-medium text-[var(--text)]">Footer text</div>
          <TextField label="Brand tagline" value={nav.footer.tagline} onChange={(v) => setFooter("tagline", v)} disabled={!editable} textarea rows={2} testId="cms-footer-tagline" />
          <TextField label="Footer links heading" value={nav.footer.house_title} onChange={(v) => setFooter("house_title", v)} disabled={!editable} testId="cms-footer-house-title" />
          <TextField label="Promise heading" value={nav.footer.promise_title} onChange={(v) => setFooter("promise_title", v)} disabled={!editable} testId="cms-footer-promise-title" />
          <TextField label="Promise text" value={nav.footer.promise_body} onChange={(v) => setFooter("promise_body", v)} disabled={!editable} textarea rows={2} testId="cms-footer-promise-body" />
          <TextField label="Military note" value={nav.footer.military_note} onChange={(v) => setFooter("military_note", v)} disabled={!editable} testId="cms-footer-military" />
          <TextField label="Copyright line" value={nav.footer.copyright} onChange={(v) => setFooter("copyright", v)} disabled={!editable} testId="cms-footer-copyright" />
        </Card>

        <Card className="border-[var(--border-lch)] p-5 space-y-4 lg:col-span-2">
          <div className="text-sm font-medium text-[var(--text)]">Announcement bar (top of every page)</div>
          <div className="grid sm:grid-cols-3 gap-4">
            <TextField label="Lead text" value={nav.announcement.lead} onChange={(v) => setAnnc("lead", v)} disabled={!editable} testId="cms-annc-lead" />
            <TextField label="Highlighted text (gold)" value={nav.announcement.highlight} onChange={(v) => setAnnc("highlight", v)} disabled={!editable} testId="cms-annc-highlight" />
            <TextField label="Trailing text" value={nav.announcement.tail} onChange={(v) => setAnnc("tail", v)} disabled={!editable} testId="cms-annc-tail" />
          </div>
        </Card>
      </div>

      {editable && (
        <div className="flex justify-end sticky bottom-4 mt-5 max-w-4xl">
          <Button onClick={save} disabled={saving} data-testid="cms-nav-save" className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)] shadow-lg">
            {saving ? "Saving…" : "Save & Publish"}
          </Button>
        </div>
      )}

      <HistoryDialog contentKey={SITE_NAV_KEY} open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />
    </div>
  );
}
